import type { EditorHandle } from "@shared/ui/Editor/ui";
import { useState, type RefObject } from "react";

type ShowStatus = (message: string, tone?: "ok" | "err") => void;

type UseBriefDownloadArgs = {
  editorRef: RefObject<EditorHandle | null>;
  campaignName: string;
  // isImporting: boolean;
  showStatus: ShowStatus;
  onMenuClose: () => void;
};

export function useBriefDownload({
  editorRef,
  campaignName,
  // isImporting,
  showStatus,
  onMenuClose,
}: UseBriefDownloadArgs) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadLabel, setDownloadLabel] = useState("Preparing download…");

  const runDownload = async (
    kind: "doc" | "pdf",
    action: () => Promise<{ mediaFailed: number }>
  ) => {
    if (!editorRef.current) {
      showStatus("Editor is not ready", "err");
      return;
    }
    // if (isDownloading || isImporting) return;
    if (isDownloading) return;

    onMenuClose();
    setIsDownloading(true);
    setDownloadLabel(kind === "pdf" ? "Preparing PDF…" : "Preparing DOC…");
    showStatus(
      kind === "pdf"
        ? "Preparing PDF — fetching media…"
        : "Preparing DOC — fetching media…"
    );

    try {
      const result = await action();
      if (result.mediaFailed > 0) {
        showStatus(
          `Downloaded ${kind.toUpperCase()}, but ${result.mediaFailed} image(s) could not be embedded`,
          "err"
        );
      } else {
        showStatus(`Downloaded as ${kind.toUpperCase()}`);
      }
    } catch {
      showStatus(`Could not download ${kind.toUpperCase()}`, "err");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadDoc = () =>
    runDownload("doc", () => editorRef.current!.downloadDoc(campaignName));

  const handleDownloadPdf = () =>
    runDownload("pdf", () => editorRef.current!.downloadPdf(campaignName));

  return {
    isDownloading,
    downloadLabel,
    handleDownloadDoc,
    handleDownloadPdf,
  };
}
